/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { BaseAPI } from '../../../shared/infra/api/BaseAPI';
import { right, left } from '../../../shared/core/Either';
import { Result } from '../../../shared/core/Result';
import { APIResponse } from '../../../shared/infra/api/APIResponse';
import { LoginDTO } from '../dtos/Login.dto';
import { LoginRequest } from '../infra/types/LoginRequest.type';
import { IAuthService } from './authService';
import { UserResponse, User } from '../models/user';
import { AxiosError } from 'axios';
import {
  UpdateUserPasswordRequestDTO,
  UpdateUserProfileRequestDTO,
} from '../../admin/settings/infra';

export interface IUserService {
  getCurrentUser(): Promise<APIResponse<User>>;
  login(loginRequest: LoginRequest): Promise<APIResponse<LoginDTO>>;
  logout(): Promise<APIResponse<void>>;
  hasToken(): boolean;
  updateProfile(
    profileRequest: UpdateUserProfileRequestDTO,
  ): Promise<APIResponse<void>>;
  updatePassword(
    passwordRequest: UpdateUserPasswordRequestDTO,
  ): Promise<APIResponse<void>>;
}

export class UserService extends BaseAPI implements IUserService {
  protected onFailureExecuteImpl(error: AxiosError<any>): Promise<any> {
    throw new Error(`Method not implemented. ${error.name}`);
  }

  constructor(authService: IAuthService) {
    super(authService);
  }

  hasToken(): boolean {
    return this.authService.isUserAuthenticated();
  }

  async getCurrentUser(): Promise<APIResponse<User>> {
    try {
      const response = await this.get<UserResponse>({
        url: '/v1/users/me',
        requiresToken: true,
      });
      const user = response.data.user;

      return right(Result.ok<User>(user));
    } catch (err: any) {
      return left({
        message: err.response ? err.response.data.message : 'Connection failed',
      });
    }
  }

  async logout(): Promise<APIResponse<void>> {
    try {
      await this.post({
        url: '/v1/users/logout',
        requiresToken: true,
      });

      this.authService.removeAccessToken();
      return right(Result.ok<void>());
    } catch (err: any) {
      return left({
        message: err.response ? err.response.data.message : 'Connection failed',
      });
    }
  }

  async login(loginRequest: LoginRequest): Promise<APIResponse<LoginDTO>> {
    try {
      const response = await this.post<LoginDTO>({
        url: '/v1/users/login',
        requiresToken: false,
        payload: loginRequest,
      });
      const dto = response.data;

      this.authService.setAccessToken(dto.accessToken);
      this.authService.setAccessExp(dto.exp);
      return right(Result.ok<LoginDTO>(dto));
    } catch (err: any) {
      return left({
        message: err.response ? err.response.data.message : 'Connection failed',
      });
    }
  }

  async updateProfile(
    profileRequest: UpdateUserProfileRequestDTO,
  ): Promise<APIResponse<void>> {
    try {
      await this.patch<UpdateUserProfileRequestDTO>({
        url: '/v1/users/me/profile',
        requiresToken: true,
        payload: profileRequest,
      });

      return right(Result.ok<void>());
    } catch (err: any) {
      return left({
        message: err.response ? err.response.data.message : 'Connection failed',
      });
    }
  }

  async updatePassword(
    passwordRequest: UpdateUserPasswordRequestDTO,
  ): Promise<APIResponse<void>> {
    try {
      await this.patch<UpdateUserPasswordRequestDTO>({
        url: '/v1/users/me/password',
        requiresToken: true,
        payload: passwordRequest,
      });

      return right(Result.ok<void>());
    } catch (err: any) {
      return left({
        message: err.response ? err.response.data.message : 'Connection failed',
      });
    }
  }
}
